import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import type { ProgressMilestone } from "../../../components/UIToolkit/Graphs/MilestoneProgressBar/MilestoneProgressBar";
import type { PbjPlanActionProgress, PbjPlanCost, PbjPlanValue } from "../../../utils/models";

/**
 * Which attribute of a `PbjPlanValue` the tracker is currently showing.
 *
 * The endpoint ships every number in BOTH bases, so switching is an attribute read — never a
 * multiplication. One conversion factor cannot serve a per-day rate, a per-week rate and a
 * cumulative quarter total at once, because each divides by a different denominator.
 */
export type PlanBasis = "hours" | "hppd";

export const planValueOf = (value: PbjPlanValue, basis: PlanBasis): number => (basis === "hours" ? value.hours : value.hppd);

/**
 * The typographic minus (U+2212). The rest of the feature's copy prints negative figures with it, and
 * an ASCII hyphen beside those reads as a dash rather than a sign.
 */
export const MINUS_SIGN = "\u2212";

/**
 * A negative figure wrapped in a left-to-right isolate (LRI … PDI). In a right-to-left sentence the
 * bidi algorithm otherwise moves the sign to the far side of the digits ("0.188−"). Invisible in LTR,
 * and only the signed number is isolated — never the unit or the words around it.
 */
const isolateNegative = (digits: string): string => `\u2066${MINUS_SIGN}${digits}\u2069`;

/**
 * Hour counts read as quantities (one decimal is enough for a figure in the hundreds); HPPD is a
 * small rate whose meaningful movement lives in the third decimal, matching the Prediction tab.
 */
const PLAN_DECIMALS: Record<PlanBasis, number> = { hours: 1, hppd: 3 };

/**
 * A plan figure rounded to the precision it is printed at. Every sign decision is taken on THIS, not
 * on the raw float: a cut lever's `−1 × 0` is `-0`, and −0.04 hours rounds to zero, and either one
 * tested raw printed "-0 hrs" on a card that had simply not moved.
 */
export const roundPlanNumber = (value: number, basis: PlanBasis): number => {
	const factor = 10 ** PLAN_DECIMALS[basis];
	// Rounded as a magnitude so a half rounds away from zero on both sides, as `toLocaleString` does.
	const magnitude = Math.round(Math.abs(value) * factor) / factor;
	// A literal `0`, never `Math.sign(value) * 0`, which is `-0` for any negative input.
	return magnitude === 0 ? 0 : Math.sign(value) * magnitude;
};

export const formatPlanNumber = (value: number, basis: PlanBasis): string => {
	const rounded = roundPlanNumber(value, basis);
	const decimals = PLAN_DECIMALS[basis];
	const magnitude = Math.abs(rounded);
	const digits = basis === "hours" ? magnitude.toLocaleString(undefined, { maximumFractionDigits: decimals }) : magnitude.toFixed(decimals);
	return rounded < 0 ? isolateNegative(digits) : digits;
};

/** Whole points (or a whole percent), signed with the true minus (bidi-isolated) and never printed as "-0". */
export const formatWholeNumber = (value: number): string => {
	const magnitude = Math.round(Math.abs(value));
	return value < 0 && magnitude !== 0 ? isolateNegative(`${magnitude}`) : `${magnitude}`;
};

export const formatPlanValue = (value: PbjPlanValue, basis: PlanBasis): string => formatPlanNumber(planValueOf(value, basis), basis);

/**
 * Whole hours, for the context chips (manual and estimated totals). Deliberately coarser than
 * `formatPlanNumber`: these sit beside the measured figures without summing to them, and a decimal
 * invites a reconciliation that was never on offer.
 */
export const formatWholeHours = (value: number): string => value.toLocaleString(undefined, { maximumFractionDigits: 0 });

export const formatCurrency = (value: number): string => {
	// Whole dollars, rounded first so a cent of float residue — or a cut's `−price` of a zero price —
	// prints "$0" rather than "-$0".
	const rounded = Math.round(value);
	return (rounded === 0 ? 0 : rounded).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
};

/**
 * Dates on the progress payload are already Luxon `DateTime`s — the axios response interceptor
 * parses every ISO date in a GET body. Re-parsing one with `DateTime.fromISO` yields an Invalid
 * DateTime, so this takes the parsed value and only formats it.
 */
export const formatPlanDate = (value: DateTime): string => value.toLocaleString(DateTime.DATE_MED);

/**
 * Whether an action's on-track verdict carries any information yet.
 *
 * `onTrack` is `delivered >= requiredToDate`, and BOTH sides are charged over completed weeks only.
 * Before the first week closes `delivered` is exactly 0 (a sum over no weeks) and `requiredToDate` is
 * exactly 0 (`goalTotal x 0 / planDays`), so `0 >= 0` makes the verdict unconditionally true — a green
 * badge guaranteed for every feasible lever in week 0, however little is arriving. It reports the
 * absence of a requirement as success, which is how a card came to read "On track" beside a forecast
 * landing 10,820 hours short of its goal.
 *
 * The same reasoning the Investment panel withholds `onBudget` under: a verdict whose comparison is
 * vacuous is not shown, because a reader cannot tell a trivially-true one from an earned one.
 */
export const isActionJudged = (days: { covered: number }): boolean => days.covered > 0;

/**
 * Where a milestone sits on an action's progress bar, as a percentage of the committed lift.
 *
 * Positioned in HOURS, in both bases. Every hour the plan delivers lands inside the quarter, so
 * `milestone.hours` (what the edge costs) over `goalTotal.hours` (what the plan commits) is exactly
 * "what share of this commitment reaches that edge" — the same axis `progressPct` fills, which is
 * `deliveredToDate.hours / goalTotal.hours`.
 *
 * Reported HPPD is NOT interchangeable here, though it looks it. `goalTotal.hppd` is normalised over
 * the PLAN WINDOW's applicable days while `hppdGap` is the lift required across the whole QUARTER, so
 * the ratio came out scaled by `plan_days / quarter_days` and squeezed every marker toward the left:
 * on a plan saved seven weeks into a quarter, an edge costing 11,894 hours of a 12,169-hour
 * commitment was drawn at 46% instead of 98%, and three edges the plan cannot afford at all were
 * drawn comfortably inside it.
 *
 * Hours are used for the geometry in BOTH bases deliberately — the toggle changes which figure is
 * printed on the marker, never where the marker sits, so the bar cannot reflow when it is flipped.
 */
export const milestonePct = (milestoneHours: number, goalTotalHours: number): number =>
	// `!== 0`, not `> 0`: a cut's commitment and its floors are BOTH negative, so the ratio is still a
	// positive share of the cut. Testing `> 0` sent every cut milestone to infinity — off the bar and
	// miscounted as "beyond plan".
	goalTotalHours !== 0 ? (milestoneHours / goalTotalHours) * 100 : Number.POSITIVE_INFINITY;

export type PlanDirection = PbjPlanActionProgress["direction"];

/**
 * +1 for an add lever, −1 for a cut. Every hours/HPPD figure on a cut card is negative when the cut
 * is being delivered, so multiplying by this reads it IN THE LEVER'S DIRECTION: "40 hrs cut" is +40,
 * and staffing that rose above baseline on a cut lever is a negative cut.
 *
 * Deliberately not `Math.abs`: that would print a cut moving backwards as progress, the same mistake
 * as showing an add that fell below baseline as a positive delivery.
 */
export const directionSign = (direction: PlanDirection): 1 | -1 => (direction === "cut" ? -1 : 1);

/** A plan value read in the lever's direction — see `directionSign`. Identical to `formatPlanValue` for an add. */
export const formatPlanValueInDirection = (value: PbjPlanValue, basis: PlanBasis, direction: PlanDirection): string =>
	formatPlanNumber(directionSign(direction) * planValueOf(value, basis), basis);

/**
 * Whether the lever moved AGAINST its commitment — an add whose staffing fell below baseline, or a cut
 * whose staffing rose above it. Judged at print precision, so a card never says "moved backwards"
 * beside a figure that reads 0.
 */
export const movedBackwards = (action: Pick<PbjPlanActionProgress, "delivered" | "direction">, basis: PlanBasis): boolean =>
	roundPlanNumber(directionSign(action.direction) * planValueOf(action.delivered, basis), basis) < 0;

/** How an action's figures are printed: the basis shown and its already-translated unit. */
export type ActionFormat = { basis: PlanBasis; unit: string };

export const withUnit = (text: string, unit: string): string => `${text} ${unit}`;

/**
 * A floor is a risk, and whether the cut has fallen through it is said in words as well as hue — the
 * screen-reader text the bar prints beside the marker's label.
 */
const cutMilestoneState = (crossed: boolean, t: TFunction): Pick<ProgressMilestone, "tone" | "stateLabel"> => ({
	tone: "risk",
	stateLabel: t(crossed ? "pbj_model.tracker.milestone_state_crossed_cut" : "pbj_model.tracker.milestone_state_ahead_cut"),
});

/**
 * The band-edge markers on an action's bar, on the same 0–100 axis as `progressPct` — both are a share
 * of the committed lift. Points come from CROSSING an edge, so each marker is a discrete step; nothing
 * is interpolated between them.
 *
 * Position and the achieved test are both charged in HOURS, against the very figure the marker prints
 * (see `milestonePct` for why reported HPPD squeezed every marker leftward).
 *
 * On a cut the markers are FLOORS beneath the department, each costing points if the cut falls through
 * it (`points`, `hours` and `hppdGap` all negative). They are drawn as risks, not rewards — warning
 * ahead of the fill, error once crossed — and "crossed" is read in the cut's direction.
 */
export const buildActionMilestones = (action: PbjPlanActionProgress, { basis, unit }: ActionFormat, t: TFunction): ProgressMilestone[] => {
	const isCut = action.direction === "cut";
	const sign = directionSign(action.direction);
	return action.milestones.map((milestone, index) => {
		const achieved = sign * action.deliveredToDate.hours >= sign * milestone.hours;
		return {
			id: `${milestone.categoryId}-${milestone.star}-${index}`,
			pct: milestonePct(milestone.hours, action.goalTotal.hours),
			label: t(isCut ? "pbj_model.tracker.milestone_points_cut" : "pbj_model.tracker.milestone_points", {
				value: formatWholeNumber(sign * milestone.points),
			}),
			sublabel: withUnit(formatPlanNumber(sign * (basis === "hours" ? milestone.hours : milestone.hppdGap), basis), unit),
			achieved,
			tooltip: t(isCut ? "pbj_model.tracker.milestone_tooltip_cut" : "pbj_model.tracker.milestone_tooltip", {
				star: milestone.star,
				points: formatWholeNumber(sign * milestone.points),
				// Plural selector: the magnitude the sentence counts, whatever the lever's direction.
				count: Math.round(Math.abs(milestone.points)),
			}),
			...(isCut ? cutMilestoneState(achieved, t) : {}),
		};
	});
};

/**
 * The SPEND side's budget to date. The backend's `budgetToDate` is judged on the NET basis
 * (`netPrice × elapsedShare`), which on a mixed plan is the cost minus the savings — not what the
 * spend bar alone should be read against. Same ramp, applied to the cost side only: a share of the
 * plan's own figures, not a re-derivation of any wage math.
 */
export const spendBudgetToDate = (cost: Pick<PbjPlanCost, "totalPrice" | "elapsedShare">): number => cost.totalPrice * cost.elapsedShare;
